import { useCallback, useMemo, useState } from "react";

import { useGetCustomerStats, useGetCustomers } from "@/api";
import type { GetCustomersParams } from "@/api/schemas/getCustomersParams";
import { useSessionStore } from "@/stores/session.store";

const PER_PAGE = 10;

export function useCustomersList() {
  const activeStoreId = useSessionStore((state) => state.activeStoreId);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);

  const params = useMemo((): GetCustomersParams | undefined => {
    if (activeStoreId === null) return undefined;

    return {
      store_id: activeStoreId,
      page,
      per_page: PER_PAGE,
      search: search.trim() || undefined,
    };
  }, [activeStoreId, page, search]);

  const { data, isLoading, isError } = useGetCustomers(params, {
    query: { enabled: activeStoreId !== null },
  });

  const { data: statsData, isLoading: isStatsLoading } = useGetCustomerStats(
    activeStoreId !== null ? { store_id: activeStoreId } : undefined,
    { query: { enabled: activeStoreId !== null } },
  );

  const customers = data?.data?.data ?? [];
  const meta = data?.data?.meta;
  const stats = statsData?.data?.data;

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  return {
    customers,
    meta,
    stats,
    page,
    setPage,
    search,
    setSearch: handleSearchChange,
    selectedCustomerId,
    setSelectedCustomerId,
    isLoading,
    isStatsLoading,
    isError,
    isEmpty: !isLoading && customers.length === 0,
  };
}
